import React, { useEffect, useState } from 'react'
import { useI18n } from '../../i18n/I18nContext'
import { adminService } from '../../services/admin'

type ShiftMode = 'csv' | 'calendar'

type Preview = {
  headers: string[]
  rows: string[][]
  totalRows: number
}

type ShiftRow = {
  rowNumber?: number
  workerId: string
  locationKey: string
  startTime: string
  earliestAllowed?: string | null
  metadata?: Record<string, unknown>
}

const weekRange = () => {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(start.getDate() + 7)
  return { startDate: start.getTime(), endDate: end.getTime() }
}

export const ShiftImport: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t, formatDateTime } = useI18n()
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvText, setCsvText] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [mode, setMode] = useState<ShiftMode>('csv')
  const [shifts, setShifts] = useState<ShiftRow[]>([])
  const [draftShift, setDraftShift] = useState<ShiftRow>({ workerId: '', locationKey: '', startTime: '' })
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const readFile = async (file: File) => {
    const text = await file.text()
    setCsvText(text)
    setPreview(null)
    setStatus('')
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    setCsvFile(file)
    setError('')
    if (file) {
      await readFile(file)
    } else {
      setCsvText('')
      setPreview(null)
    }
  }

  const previewCsv = async () => {
    if (!csvText) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.previewShiftCsv(csvText)
      setPreview(response.data as Preview)
      setStatus(t('Previewed {count} rows.', { count: response.data.totalRows || 0 }))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to preview CSV.'))
    } finally {
      setLoading(false)
    }
  }

  const importCsv = async () => {
    if (!csvText) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.importShiftCsv(csvText)
      setStatus(t('Imported {imported} of {total} rows.', { imported: response.data.importedCount || 0, total: response.data.totalRows || 0 }))
      await loadCalendar()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to import shifts.'))
    } finally {
      setLoading(false)
    }
  }

  const loadCalendar = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.listShiftCalendar(weekRange())
      setShifts((response.data.shifts || []) as ShiftRow[])
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to load shifts.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (mode === 'calendar') {
      loadCalendar()
    }
  }, [mode])

  const saveDraftShift = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!draftShift.workerId || !draftShift.locationKey || !draftShift.startTime) {
      setError(t('Worker, location, and start time are required.'))
      return
    }
    setLoading(true)
    setError('')
    try {
      await adminService.saveShiftAdjustments([{ ...draftShift, rowNumber: Date.now(), metadata: draftShift.metadata || {} }])
      setDraftShift({ workerId: '', locationKey: '', startTime: '' })
      setStatus(t('Shift saved.'))
      await loadCalendar()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to save shift.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className={embedded ? 'stack' : 'panel workspace-panel stack'} data-testid="workspace-panel">
      {!embedded ? (
        <div className="stack" style={{ gap: '.4rem' }}>
          <h2 className="heading-lg">{t('Shift import workspace')}</h2>
          <p className="text-muted">{t('Upload worker schedules, preview mapped rows, and adjust calendar records.')}</p>
        </div>
      ) : null}

      <div className="inline-group" role="tablist" aria-label={t('Shift tool mode')}>
        <button type="button" className={`btn ${mode === 'csv' ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={mode === 'csv'} onClick={() => setMode('csv')} data-testid="tab-csv-import">{t('CSV import')}</button>
        <button type="button" className={`btn ${mode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={mode === 'calendar'} onClick={() => setMode('calendar')} data-testid="tab-calendar-editor">{t('Calendar editor')}</button>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {status ? <p className="helper">{status}</p> : null}

      {mode === 'csv' ? (
        <div className="stack">
          <div className="form-row">
            <label htmlFor="shiftCsv">{t('Upload worker shift CSV')}</label>
            <input id="shiftCsv" type="file" accept=".csv" onChange={handleFileChange} className="input" data-testid="shift-csv-input" />
            <p className="helper">{csvFile ? t('Selected: {file}', { file: csvFile.name }) : t('No file selected.')}</p>
          </div>

          <div className="inline-group">
            <button type="button" disabled={!csvText || loading} className="btn btn-secondary" onClick={previewCsv}>{t('Preview CSV')}</button>
            <button type="button" disabled={!csvText || loading} className="btn btn-primary" onClick={importCsv} data-testid="import-shifts-button">{t('Import shifts')}</button>
          </div>

          {preview ? (
            <div className="panel" style={{ overflowX: 'auto' }}>
              <table className="workspace-table">
                <thead>
                  <tr>{preview.headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, index) => (
                    <tr key={`${index}-${row.join('|')}`}>
                      {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : (
        <section className="stack" data-testid="calendar-editor-panel">
          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={saveDraftShift}>
            <h3 style={{ margin: 0 }}>{t('Add or adjust shift')}</h3>
            <div className="mapper-grid">
              <div className="form-row">
                <label htmlFor="workerId">{t('Worker ID or phone')}</label>
                <input id="workerId" className="input" value={draftShift.workerId} onChange={(event) => setDraftShift({ ...draftShift, workerId: event.target.value })} />
              </div>
              <div className="form-row">
                <label htmlFor="locationKey">{t('Location key')}</label>
                <input id="locationKey" className="input" value={draftShift.locationKey} onChange={(event) => setDraftShift({ ...draftShift, locationKey: event.target.value })} />
              </div>
              <div className="form-row">
                <label htmlFor="startTime">{t('Start time')}</label>
                <input id="startTime" className="input" type="datetime-local" value={draftShift.startTime} onChange={(event) => setDraftShift({ ...draftShift, startTime: event.target.value })} />
              </div>
              <div className="form-row">
                <label htmlFor="earliestAllowed">{t('Earliest allowed')}</label>
                <input id="earliestAllowed" className="input" type="datetime-local" value={draftShift.earliestAllowed || ''} onChange={(event) => setDraftShift({ ...draftShift, earliestAllowed: event.target.value || null })} />
              </div>
            </div>
            <div className="inline-group">
              <button type="submit" className="btn btn-primary" disabled={loading}>{t('Save shift')}</button>
              <button type="button" className="btn btn-secondary" disabled={loading} onClick={loadCalendar}>{t('Refresh calendar')}</button>
            </div>
          </form>

          <div className="panel" style={{ overflowX: 'auto' }}>
            <table className="workspace-table">
              <thead>
                <tr><th>{t('Worker')}</th><th>{t('Location')}</th><th>{t('Start')}</th><th>{t('Earliest')}</th></tr>
              </thead>
              <tbody>
                {shifts.length === 0 ? (
                  <tr><td colSpan={4} className="helper">{t('No shifts found.')}</td></tr>
                ) : shifts.map((shift, index) => (
                  <tr key={`${shift.workerId}-${shift.locationKey}-${shift.startTime}-${index}`}>
                    <td>{shift.workerId}</td>
                    <td>{shift.locationKey}</td>
                    <td>{formatDateTime(shift.startTime)}</td>
                    <td>{shift.earliestAllowed ? formatDateTime(shift.earliestAllowed) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </section>
  )
}
